import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { ImageField } from "./ImageField";
import { useAdminT } from "./adminI18n";

const inputCls =
  "w-full bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-3 focus:outline-none focus:border-luxury-gold";

const emptyPkg = () => ({
  id: `pkg-${Date.now()}`,
  title: "",
  title_en: "",
  duration: "",
  duration_en: "",
  price: 0,
  currency: "usd",
  booking_type: "whatsapp",
  is_active: true,
  is_popular: false,
});

const BOOKING_TYPES = [
  { value: "whatsapp", label: "حجز عبر واتساب" },
  { value: "payment_link", label: "رابط دفع (Stripe Payment Link)" },
  { value: "contact", label: "نموذج التواصل" },
];

export const ConsultationsTab = () => {
  const { t } = useAdminT();
  const [form, setForm] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((res) => setForm(res.data))
      .catch(() => toast.error(t("تعذر تحميل الإعدادات")));
    axios
      .get(`${API}/admin/bookings`, { headers: authHeaders() })
      .then((res) => setBookings(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [t]);

  if (!form) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>;
  }

  const c = form.consultation || { packages: [] };
  const packages = c.packages || [];
  const setC = (patch) => setForm({ ...form, consultation: { ...c, ...patch } });
  const setPkg = (i, patch) => {
    const next = [...packages];
    next[i] = { ...next[i], ...patch };
    setC({ packages: next });
  };
  const addPkg = () => setC({ packages: [...packages, emptyPkg()] });
  const delPkg = (i) => setC({ packages: packages.filter((_, x) => x !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        consultation: {
          ...c,
          packages: packages.map((p) => ({ ...p, price: Number(p.price) || 0 })),
        },
      };
      await axios.put(`${API}/admin/settings`, payload, { headers: authHeaders() });
      toast.success(t("تم حفظ قسم الاستشارات — حدّثي الصفحة الرئيسية لرؤية التغييرات"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form data-testid="admin-consultations-form" onSubmit={save} className="space-y-10">
      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-5">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("قسم الاستشارات الخاصة أونلاين")}</h2>
        <p className="text-luxury-gray text-sm">{t("العنوان والنص التعريفي والصورة التي تظهر في قسم «الاستشارات الخاصة» على الصفحة الرئيسية.")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><label className="block text-sm text-luxury-gray mb-2">{t("العنوان (عربي)")}</label><input data-testid="consult-title" value={c.title || ""} onChange={(e) => setC({ title: e.target.value })} className={inputCls} /></div>
          <div><label className="block text-sm text-luxury-gray mb-2">Title (EN)</label><input dir="ltr" data-testid="consult-title-en" value={c.title_en || ""} onChange={(e) => setC({ title_en: e.target.value })} className={inputCls} /></div>
        </div>
        <div><label className="block text-sm text-luxury-gray mb-2">{t("النص التعريفي (عربي)")}</label><textarea data-testid="consult-caption" value={c.caption || ""} onChange={(e) => setC({ caption: e.target.value })} rows={4} className={inputCls} /></div>
        <div><label className="block text-sm text-luxury-gray mb-2">Caption (EN)</label><textarea dir="ltr" value={c.caption_en || ""} onChange={(e) => setC({ caption_en: e.target.value })} rows={4} className={inputCls} /></div>
        <ImageField testid="consult-image-field" label={t("صورة القسم (مكتب / إطلالة دبي)")} value={c.image} onChange={(v) => setC({ image: v })} />
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("باقات الجلسات")}</h2>
            <p className="text-luxury-gray text-sm">{t("أضيفي وعدّلي واحذفي الباقات كما تشائين — عدد غير محدود. إذا كان السعر ٠ تظهر عبارة «السعر عند التواصل».")}</p>
          </div>
          <button type="button" data-testid="consult-add-package" onClick={addPkg} className="flex items-center gap-2 border border-luxury-gold text-luxury-gold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-gold hover:text-luxury-black transition-colors">
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            {t("إضافة باقة")}
          </button>
        </div>
        {packages.map((p, i) => (
          <div key={p.id} data-testid={`consult-pkg-${i}`} className="border border-luxury-border p-6 space-y-4 bg-luxury-black/40">
            <div className="flex items-center justify-between">
              <p className="text-luxury-gold font-semibold">{t("باقة")} {i + 1}</p>
              <button type="button" data-testid={`consult-pkg-delete-${i}`} onClick={() => delPkg(i)} className="text-luxury-gray hover:text-red-400 transition-colors">
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="block text-xs text-luxury-gray mb-1">{t("اسم الباقة (عربي)")}</label><input value={p.title || ""} onChange={(e) => setPkg(i, { title: e.target.value })} className={inputCls} /></div>
              <div><label className="block text-xs text-luxury-gray mb-1">Name (EN)</label><input dir="ltr" value={p.title_en || ""} onChange={(e) => setPkg(i, { title_en: e.target.value })} className={inputCls} /></div>
              <div><label className="block text-xs text-luxury-gray mb-1">{t("المدة / الوصف (عربي)")}</label><input value={p.duration || ""} onChange={(e) => setPkg(i, { duration: e.target.value })} className={inputCls} placeholder={t("45 دقيقة")} /></div>
              <div><label className="block text-xs text-luxury-gray mb-1">Duration (EN)</label><input dir="ltr" value={p.duration_en || ""} onChange={(e) => setPkg(i, { duration_en: e.target.value })} className={inputCls} placeholder="45 minutes" /></div>
              <div><label className="block text-xs text-luxury-gray mb-1">{t("السعر (0 = عند التواصل)")}</label><input dir="ltr" type="number" min="0" step="0.01" value={p.price ?? 0} onChange={(e) => setPkg(i, { price: e.target.value })} className={inputCls} /></div>
              <div><label className="block text-xs text-luxury-gray mb-1">{t("العملة")}</label><input dir="ltr" value={p.currency || "usd"} onChange={(e) => setPkg(i, { currency: e.target.value })} className={inputCls} /></div>
              <div>
                <label className="block text-xs text-luxury-gray mb-1">{t("طريقة الحجز")}</label>
                <select data-testid={`consult-pkg-booking-${i}`} value={p.booking_type || "whatsapp"} onChange={(e) => setPkg(i, { booking_type: e.target.value })} className={inputCls}>
                  {BOOKING_TYPES.map((b) => (
                    <option key={b.value} value={b.value}>{t(b.label)}</option>
                  ))}
                </select>
              </div>
              {(p.booking_type === "payment_link") && (
                <div className="md:col-span-2">
                  <label className="block text-xs text-luxury-gray mb-1">{t("رابط الدفع (buy.stripe.com/...)")}</label>
                  <input dir="ltr" data-testid={`consult-pkg-link-${i}`} value={p.payment_link || ""} onChange={(e) => setPkg(i, { payment_link: e.target.value })} className={inputCls} placeholder="https://buy.stripe.com/..." />
                </div>
              )}
              <div className="flex items-end gap-6 pb-1">
                <label className="flex items-center gap-2 text-sm text-luxury-bone cursor-pointer">
                  <input type="checkbox" checked={!!p.is_active} onChange={(e) => setPkg(i, { is_active: e.target.checked })} className="accent-luxury-gold w-4 h-4" />
                  {t("ظاهرة على الموقع")}
                </label>
                <label className="flex items-center gap-2 text-sm text-luxury-bone cursor-pointer">
                  <input type="checkbox" checked={!!p.is_popular} onChange={(e) => setPkg(i, { is_popular: e.target.checked })} className="accent-luxury-gold w-4 h-4" />
                  {t("الأكثر طلباً")}
                </label>
              </div>
            </div>
          </div>
        ))}
        {!packages.length && <p className="text-luxury-gray text-sm">{t("لا توجد باقات — اضغطي «إضافة باقة» لبدء الإضافة.")}</p>}
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8">
        <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("الحجوزات المدفوعة")}</h2>
        <p className="text-luxury-gray text-sm mb-6">{t("الباقات المدفوعة عبر Stripe تظهر هنا ويصلك إشعار بريدي عند كل حجز.")}</p>
        {bookings.length ? (
          <div data-testid="consult-bookings-list" className="divide-y divide-luxury-border border border-luxury-border">
            {bookings.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 text-sm">
                <div>
                  <p className="text-luxury-bone font-semibold">{b.package_title}</p>
                  <p className="text-luxury-gray text-xs" dir="ltr">{b.email || t("زائرة")}</p>
                </div>
                <div className="text-left">
                  <p className="text-luxury-gold font-semibold" dir="ltr">{b.amount} {(b.currency || "usd").toUpperCase()}</p>
                  <p className="text-luxury-gray text-xs" dir="ltr">{(b.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p data-testid="consult-bookings-empty" className="text-luxury-gray text-sm">{t("لا توجد حجوزات مدفوعة بعد.")}</p>
        )}
      </section>

      <button data-testid="consult-save-button" type="submit" disabled={saving} className="flex items-center gap-3 bg-luxury-gold text-luxury-black font-semibold px-10 py-4 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" strokeWidth={1.5} />}
        {t("حفظ قسم الاستشارات")}
      </button>
    </form>
  );
};
